import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Plus, Trash2, Send, Save, DollarSign, 
  User, Mail, Phone, MapPin, FileText, Calculator,
  ChevronDown, ChevronUp, Copy, Eye, X, Percent, Home, AlertTriangle, Loader
} from 'lucide-react';
import { formatCurrency } from '../utils/helpers';
import './CustomEstimate.css';

// Common UOM options
const UOM_OPTIONS = [
  { value: 'each', label: 'Each' },
  { value: 'hour', label: 'Hour' },
  { value: 'sqft', label: 'Sq Ft' },
  { value: 'room', label: 'Room' },
  { value: 'floor', label: 'Floor' },
  { value: 'window', label: 'Window' },
  { value: 'visit', label: 'Visit' },
  { value: 'month', label: 'Month' },
  { value: 'flat', label: 'Flat Rate' },
];

// Preset line items for quick add
const PRESET_ITEMS = [
  { category: 'Cleaning Services', items: [
    { description: 'Standard Cleaning', uom: 'visit', price: 150 },
    { description: 'Deep Clean', uom: 'visit', price: 300 },
    { description: 'Move In/Out Clean', uom: 'flat', price: 400 },
    { description: 'Post-Construction Clean', uom: 'hour', price: 50 },
  ]},
  { category: 'Add-On Services', items: [
    { description: 'Inside Oven', uom: 'each', price: 35 },
    { description: 'Inside Refrigerator', uom: 'each', price: 35 },
    { description: 'Interior Windows', uom: 'window', price: 8 },
    { description: 'Baseboards Detail', uom: 'room', price: 15 },
    { description: 'Inside Cabinets', uom: 'each', price: 25 },
  ]},
  { category: 'Commercial', items: [
    { description: 'Office Cleaning', uom: 'sqft', price: 0.15 },
    { description: 'Restroom Service', uom: 'each', price: 25 },
    { description: 'Floor Stripping & Waxing', uom: 'sqft', price: 0.75 },
    { description: 'Carpet Shampooing', uom: 'sqft', price: 0.35 },
  ]},
  { category: 'Specialty', items: [
    { description: 'Hoarding Cleanout', uom: 'hour', price: 75 },
    { description: 'Junk Removal', uom: 'hour', price: 85 },
    { description: 'Organizing Services', uom: 'hour', price: 65 },
    { description: 'Estate Cleanout', uom: 'flat', price: 0 },
  ]},
];

function CustomEstimate() {
  const navigate = useNavigate();
  const { id: estimateId } = useParams(); // Get estimate ID from URL if editing
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { state, getClientById, getEstimateById, createEstimate, updateEstimate, deleteEstimate, addClient, addProperty, logClientCommunication } = useApp();
  
  const preSelectedClientId = searchParams.get('clientId');
  const isEditing = !!estimateId;
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.email === 'sarah@twosisterscleaningco.com' || user?.email === 'sarah.keel@twosisterscleaningco.com';
  
  // Load existing estimate if editing
  const existingEstimate = isEditing ? getEstimateById(estimateId) : null;
  
  // Client selection
  const [selectedClientId, setSelectedClientId] = useState(preSelectedClientId || '');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // New client form
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClientInfo, setNewClientInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  
  // Property/Address info
  const [propertyInfo, setPropertyInfo] = useState({
    street: '',
    city: '',
    state: 'VA',
    zip: '',
    sqft: '',
    bedrooms: '',
    bathrooms: '',
    propertyNotes: ''
  });
  
  // Billing address
  const [useDifferentBilling, setUseDifferentBilling] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    city: '',
    state: 'VA',
    zip: ''
  });
  
  // Estimate details
  const [estimateTitle, setEstimateTitle] = useState('Custom Quote');
  const [validDays, setValidDays] = useState(30);
  const [notes, setNotes] = useState('');
  
  // Discount
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  
  // Line items
  const [lineItems, setLineItems] = useState([
    { id: 1, description: '', uom: 'each', qty: 1, unitPrice: 0 }
  ]);
  
  // UI state
  const [showPresets, setShowPresets] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmChecked, setDeleteConfirmChecked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load existing estimate data when editing
  useEffect(() => {
    if (existingEstimate) {
      // Set client
      if (existingEstimate.clientId) {
        setSelectedClientId(existingEstimate.clientId);
      }
      
      // Set estimate title and details
      setEstimateTitle(existingEstimate.title || 'Custom Quote');
      setNotes(existingEstimate.notes || '');
      
      // Set discount
      if (existingEstimate.discountType && existingEstimate.discountType !== 'none') {
        setDiscountType(existingEstimate.discountType);
        setDiscountValue(existingEstimate.discountValue || 0);
        setDiscountReason(existingEstimate.discountReason || '');
      }
      
      // Set line items
      if (existingEstimate.lineItems && existingEstimate.lineItems.length > 0) {
        setLineItems(existingEstimate.lineItems.map((item, index) => ({
          id: item.id || index + 1,
          description: item.description || item.service || item._legacyDescription || '',
          uom: item.uom || 'each',
          qty: item.qty ?? item.quantity ?? 1,
          unitPrice: item.unitPrice || 0
        })));
      }
      
      // Set property info
      if (existingEstimate.propertyInfo) {
        setPropertyInfo({
          street: existingEstimate.propertyInfo.street || '',
          city: existingEstimate.propertyInfo.city || '',
          state: existingEstimate.propertyInfo.state || 'VA',
          zip: existingEstimate.propertyInfo.zip || '',
          sqft: existingEstimate.propertyInfo.sqft || '',
          bedrooms: existingEstimate.propertyInfo.bedrooms || '',
          bathrooms: existingEstimate.propertyInfo.bathrooms || '',
          propertyNotes: existingEstimate.propertyInfo.propertyNotes || ''
        });
      } else if (existingEstimate.propertyAddress) {
        // Try to parse address string
        const parts = existingEstimate.propertyAddress.split(',');
        if (parts.length >= 1) {
          setPropertyInfo(prev => ({ ...prev, street: parts[0].trim() }));
        }
      }
      
      // Set billing address if different
      if (existingEstimate.billingAddress) {
        setUseDifferentBilling(true);
        const parts = existingEstimate.billingAddress.split(',');
        if (parts.length >= 1) {
          setBillingAddress(prev => ({ ...prev, street: parts[0].trim() }));
        }
      }
    }
  }, [existingEstimate]);

  // Get selected client
  const selectedClient = selectedClientId ? getClientById(selectedClientId) : null;
  
  // Filter clients for search
  const filteredClients = useMemo(() => {
    if (!clientSearch) return state.clients.slice(0, 10);
    const search = clientSearch.toLowerCase();
    return state.clients.filter(c => 
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.phone?.includes(search) ||
      c.businessName?.toLowerCase().includes(search)
    ).slice(0, 10);
  }, [state.clients, clientSearch]);

  // Calculate totals
  const subtotal = lineItems.reduce((sum, item) => {
    return sum + (item.qty * item.unitPrice);
  }, 0);
  
  const discountAmount = discountType === 'percent' 
    ? subtotal * (discountValue / 100)
    : discountType === 'fixed' 
    ? discountValue 
    : 0;
  
  const total = subtotal - discountAmount;

  // Add new line item
  const addLineItem = () => {
    setLineItems([...lineItems, {
      id: Date.now(),
      description: '',
      uom: 'each',
      qty: 1,
      unitPrice: 0
    }]);
  };

  // Add preset item
  const addPresetItem = (preset) => {
    setLineItems([...lineItems, {
      id: Date.now(),
      description: preset.description,
      uom: preset.uom,
      qty: 1,
      unitPrice: preset.price
    }]);
    setShowPresets(false);
  };

  // Update line item
  const updateLineItem = (id, field, value) => {
    setLineItems(lineItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  // Remove line item
  const removeLineItem = (id) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter(item => item.id !== id));
    }
  };

  // Duplicate line item
  const duplicateLineItem = (item) => {
    const newItem = { ...item, id: Date.now() };
    const index = lineItems.findIndex(i => i.id === item.id);
    const newItems = [...lineItems];
    newItems.splice(index + 1, 0, newItem);
    setLineItems(newItems);
  };

  // Select client from dropdown
  const selectClient = (client) => {
    setSelectedClientId(client.id);
    setClientSearch('');
    setShowClientDropdown(false);
    setIsNewClient(false);
    
    // Pre-fill property info if client has a property
    const clientProperty = state.properties?.find(p => p.clientId === client.id && p.isPrimary);
    if (clientProperty) {
      setPropertyInfo({
        street: clientProperty.street || '',
        city: clientProperty.city || '',
        state: clientProperty.state || 'VA',
        zip: clientProperty.zip || '',
        sqft: clientProperty.sqft || '',
        bedrooms: clientProperty.bedrooms || '',
        bathrooms: clientProperty.bathrooms || '',
        propertyNotes: ''
      });
    }
  };

  // Build estimate object
  const buildEstimateData = (status) => {
    const validItems = lineItems.filter(item => item.description && item.unitPrice > 0);
    
    // Build unified line items format
    const unifiedLineItems = validItems.map((item, index) => ({
      id: `li_custom_${index}`,
      service: item.description,
      description: item.description,
      price: Math.round(item.qty * item.unitPrice * 100) / 100,
      type: 'custom',
      frequency: null,
      group: null,
      required: true, // Default required for custom estimates
      selected: true,
      unitPrice: item.unitPrice,
      qty: item.qty,
      quantity: item.qty,
      uom: item.uom || 'each',
      addOns: [],
    }));

    return {
      id: isEditing ? estimateId : `est_${Date.now()}`,
      clientId: selectedClientId || null,
      status,
      title: estimateTitle,
      lineItems: unifiedLineItems,
      // Legacy fields kept for backward compat
      subtotal,
      discountType,
      discountValue,
      discountAmount,
      discountReason,
      total,
      displayPrice: total, // Normalized price field
      notes,
      validUntil: new Date(Date.now() + validDays * 24 * 60 * 60 * 1000).toISOString(),
      propertyAddress: propertyInfo.street ? `${propertyInfo.street}, ${propertyInfo.city}, ${propertyInfo.state} ${propertyInfo.zip}` : '',
      propertyInfo: {
        ...propertyInfo,
        sqft: propertyInfo.sqft || null,
        bedrooms: propertyInfo.bedrooms || null,
        bathrooms: propertyInfo.bathrooms || null
      },
      billingAddress: useDifferentBilling ? `${billingAddress.street}, ${billingAddress.city}, ${billingAddress.state} ${billingAddress.zip}` : null,
      createdAt: isEditing && existingEstimate ? existingEstimate.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      source: 'custom_estimate_builder'
    };
  };

  // Save as draft
  const handleSaveDraft = async () => {
    setIsSaving(true);
    
    try {
      let clientId = selectedClientId;
      let propertyId = null;
      
      // Create new client if needed
      if (isNewClient && newClientInfo.firstName) {
        clientId = `client_${Date.now()}`;
        const newClient = {
          id: clientId,
          firstName: newClientInfo.firstName,
          lastName: newClientInfo.lastName || '',
          email: newClientInfo.email || '',
          phone: newClientInfo.phone?.replace(/\D/g, '') || '',
          status: 'lead',
          source: 'custom_estimate',
          createdAt: new Date().toISOString()
        };
        await addClient(newClient);
        setSelectedClientId(clientId);
        setIsNewClient(false);
      }
      
      // Create property if address provided and not editing
      if (propertyInfo.street && clientId && !isEditing) {
        propertyId = `prop_${Date.now()}`;
        const newProperty = {
          id: propertyId,
          clientId,
          addressLine1: propertyInfo.street, // Use standard field name
          street: propertyInfo.street, // Keep for backward compatibility
          city: propertyInfo.city || 'Charlottesville',
          state: propertyInfo.state || 'VA',
          zip: propertyInfo.zip || '',
          sqft: propertyInfo.sqft || null,
          bedrooms: propertyInfo.bedrooms || null,
          bathrooms: propertyInfo.bathrooms || null,
          isPrimary: true,
          createdAt: new Date().toISOString()
        };
        await addProperty(newProperty);
      }
      
      const estimate = buildEstimateData(existingEstimate?.status || 'draft');
      estimate.clientId = clientId;
      estimate.propertyId = propertyId; // Link property to estimate
      
      if (isEditing) {
        await updateEstimate(estimateId, estimate);
      } else {
        await createEstimate(estimate);
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    }
    
    setIsSaving(false);
  };

  // Send estimate
  const handleSend = async () => {
    if (!selectedClientId && !isNewClient) {
      alert('Please select or create a client');
      return;
    }
    
    if (isNewClient && (!newClientInfo.firstName || !newClientInfo.email)) {
      alert('Please enter at least a name and email for the new client');
      return;
    }
    
    const validItems = lineItems.filter(item => item.description && item.unitPrice > 0);
    if (validItems.length === 0) {
      alert('Please add at least one line item with a description and price');
      return;
    }
    
    setIsSending(true);
    
    try {
      let clientId = selectedClientId;
      let clientEmail = selectedClient?.email;
      let clientName = selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : '';
      let clientPhone = selectedClient?.phone || '';
      
      if (isNewClient) {
        clientId = `client_${Date.now()}`;
        clientEmail = newClientInfo.email;
        clientName = `${newClientInfo.firstName} ${newClientInfo.lastName}`.trim();
        clientPhone = newClientInfo.phone || '';
        
        const newClient = {
          id: clientId,
          firstName: newClientInfo.firstName,
          lastName: newClientInfo.lastName || '',
          email: newClientInfo.email,
          phone: clientPhone.replace(/\D/g, ''),
          status: 'lead',
          source: 'custom_estimate',
          createdAt: new Date().toISOString()
        };
        await addClient(newClient);
      }
      
      // Only create property if not editing (to avoid duplicates)
      let propertyId = null;
      if (propertyInfo.street && !isEditing) {
        propertyId = `prop_${Date.now()}`;
        const newProperty = {
          id: propertyId,
          clientId,
          addressLine1: propertyInfo.street, // Use standard field name
          street: propertyInfo.street, // Keep for backward compatibility
          city: propertyInfo.city || 'Charlottesville',
          state: propertyInfo.state || 'VA',
          zip: propertyInfo.zip || '',
          sqft: propertyInfo.sqft || null,
          bedrooms: propertyInfo.bedrooms || null,
          bathrooms: propertyInfo.bathrooms || null,
          isPrimary: true,
          createdAt: new Date().toISOString()
        };
        await addProperty(newProperty);
      }
      
      // Generate approval token for client portal
      const approvalToken = Math.random().toString(36).substr(2, 12) + Math.random().toString(36).substr(2, 12);
      
      const estimate = buildEstimateData('sent');
      estimate.clientId = clientId;
      estimate.propertyId = propertyId; // Link property to estimate
      estimate.clientName = clientName;
      estimate.clientEmail = clientEmail;
      estimate.clientPhone = clientPhone;
      estimate.sentAt = new Date().toISOString();
      estimate.approvalToken = approvalToken; // Add token for portal approval
      
      // Use updateEstimate when editing, createEstimate when new
      if (isEditing) {
        await updateEstimate(estimateId, estimate);
      } else {
        await createEstimate(estimate);
      }
      
      // Send email to client
      if (!clientEmail) {
        alert('Estimate saved but no email address on file — email not sent. You can resend from the estimate detail page after adding an email.');
        navigate('/admin/estimates');
        setIsSending(false);
        return;
      }
      
      const customEstLogId = `log_${Date.now()}`;
      const emailResponse = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'custom_estimate',
          data: {
            clientId,
            commLogId: customEstLogId,
            clientName,
            clientEmail,
            clientPhone,
            estimateTitle,
            lineItems: validItems.map(item => ({
              description: item.description,
              uom: item.uom,
              qty: item.qty,
              unitPrice: item.unitPrice
            })),
            subtotal,
            discountType,
            discountValue,
            discountAmount,
            discountReason,
            total,
            notes,
            validDays,
            propertyAddress: propertyInfo.street ? `${propertyInfo.street}, ${propertyInfo.city}, ${propertyInfo.state} ${propertyInfo.zip}` : '',
            estimateId: estimate.id,
            approvalLink: `${window.location.origin}/portal/approve/${approvalToken}`
          }
        })
      });

      let emailResult;
      try {
        emailResult = await emailResponse.json();
      } catch (parseError) {
        console.error('Email response parse error:', parseError);
        alert('Estimate saved but email server returned an invalid response. You can resend from the estimate detail page.');
        navigate('/admin/estimates');
        setIsSending(false);
        return;
      }

      if (!emailResponse.ok) {
        console.error('Email send failed:', emailResult);
        alert(`Estimate saved but email failed: ${emailResult.details || emailResult.error || 'Unknown error'}. You can resend from the estimate detail page.`);
      } else {
        // Log successful communication (non-blocking)
        try {
          if (clientId) {
            await logClientCommunication(clientId, {
              id: customEstLogId,
              type: 'email',
              category: 'estimate',
              subject: 'Custom Estimate Sent',
              recipient: clientEmail,
              status: 'sent',
              details: {
                estimateId: estimate.id,
                estimateTotal: total
              }
            });
          }
        } catch (logError) {
          console.warn('Communication log failed (non-blocking):', logError);
        }
        alert('Estimate sent successfully!');
      }
      
      navigate('/admin/estimates');
      
    } catch (error) {
      console.error('Error sending estimate:', error);
      alert('Failed to send estimate. Please try again.');
    }
    
    setIsSending(false);
  };

  // Preview Modal
  // Handle delete estimate
  const handleDeleteEstimate = async () => {
    if (!isEditing || !existingEstimate) return;
    
    // Check permissions for approved/converted
    if ((existingEstimate.status === 'approved' || existingEstimate.status === 'converted') && !isAdmin) {
      alert('Only administrators can delete approved or converted estimates.');
      return;
    }
    
    if (!deleteConfirmChecked) {
      alert('Please check the confirmation box to proceed.');
      return;
    }
    
    setIsDeleting(true);
    
    try {
      await deleteEstimate(estimateId, { isAdmin });
      navigate('/admin/estimates');
    } catch (error) {
      console.error('Error deleting estimate:', error);
      alert('Failed to delete estimate. Please try again.');
      setIsDeleting(false);
    }
  };

  // Preview Modal
  const PreviewModal = () => {
    const validItems = lineItems.filter(item => item.description && item.unitPrice > 0);
    const clientName = selectedClient 
      ? `${selectedClient.firstName} ${selectedClient.lastName}`
      : isNewClient 
      ? `${newClientInfo.firstName} ${newClientInfo.lastName}`.trim()
      : 'Client Name';
    
    return (
      <div className="modal-overlay" onClick={() => setPreviewMode(false)}>
        <div className="preview-modal" onClick={e => e.stopPropagation()}>
          <div className="preview-header">
            <h2>Estimate Preview</h2>
            <button className="btn-close" onClick={() => setPreviewMode(false)}>
              <X size={20} />
            </button>
          </div>
          
          <div className="preview-content">
            <div className="preview-company">
              <h1>Two Sisters Cleaning & Organizing</h1>
              <p>From Chaos to Clarity</p>
            </div>
            
            <div className="preview-title">
              <h2>{estimateTitle}</h2>
              <p>Prepared for: <strong>{clientName}</strong></p>
              {(selectedClient?.email || newClientInfo.email) && (
                <p><Mail size={14} /> {selectedClient?.email || newClientInfo.email}</p>
              )}
              {(selectedClient?.phone || newClientInfo.phone) && (
                <p><Phone size={14} /> {selectedClient?.phone || newClientInfo.phone}</p>
              )}
            </div>
            
            {propertyInfo.street && (
              <div className="preview-address">
                <h4><MapPin size={16} /> Service Address</h4>
                <p>{propertyInfo.street}</p>
                <p>{propertyInfo.city}, {propertyInfo.state} {propertyInfo.zip}</p>
                {propertyInfo.sqft && <p>{propertyInfo.sqft} sq ft</p>}
                {propertyInfo.bedrooms && <p>{propertyInfo.bedrooms} bed / {propertyInfo.bathrooms} bath</p>}
              </div>
            )}
            
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {validItems.map((item, i) => (
                  <tr key={i}>
                    <td>{item.description}</td>
                    <td>{item.qty} {item.uom}</td>
                    <td>{formatCurrency(item.unitPrice)}</td>
                    <td>{formatCurrency(item.qty * item.unitPrice)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="subtotal-row">
                  <td colSpan="3">Subtotal</td>
                  <td>{formatCurrency(subtotal)}</td>
                </tr>
                {discountAmount > 0 && (
                  <tr className="discount-row">
                    <td colSpan="3">
                      Discount {discountType === 'percent' ? `(${discountValue}%)` : ''}
                      {discountReason && <span className="discount-reason"> - {discountReason}</span>}
                    </td>
                    <td>-{formatCurrency(discountAmount)}</td>
                  </tr>
                )}
                <tr className="total-row">
                  <td colSpan="3">Total</td>
                  <td>{formatCurrency(total)}</td>
                </tr>
              </tfoot>
            </table>
            
            {notes && (
              <div className="preview-notes">
                <h4>Notes</h4>
                <p>{notes}</p>
              </div>
            )}
            
            <div className="preview-validity">
              <p>This estimate is valid for {validDays} days.</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="custom-estimate-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1>{isEditing ? 'Edit Custom Estimate' : 'Custom Estimate Builder'}</h1>
          <p className="header-subtitle">
            {isEditing ? `Editing: ${existingEstimate?.title || 'Custom Quote'}` : 'Create a custom quote with line items'}
          </p>
        </div>
      </div>

      <div className="estimate-builder">
        {/* Client Selection */}
        <section className="builder-section">
          <h2><User size={20} /> Client Information</h2>
          
          {!isNewClient ? (
            <div className="client-selector">
              <div className="search-wrapper">
                <input
                  type="text"
                  placeholder="Search clients by name, email, or phone..."
                  value={selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setSelectedClientId('');
                    setShowClientDropdown(true);
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                />
                {showClientDropdown && !selectedClientId && (
                  <div className="client-dropdown">
                    {filteredClients.map(client => (
                      <button
                        key={client.id}
                        className="client-option"
                        onClick={() => selectClient(client)}
                      >
                        <div className="client-name">
                          {client.firstName} {client.lastName}
                          {client.businessName && <span className="business-name"> ({client.businessName})</span>}
                        </div>
                        <div className="client-meta">
                          {client.email} • {client.phone}
                        </div>
                      </button>
                    ))}
                    <button
                      className="client-option new-client"
                      onClick={() => {
                        setIsNewClient(true);
                        setShowClientDropdown(false);
                      }}
                    >
                      <Plus size={16} /> Create New Client
                    </button>
                  </div>
                )}
              </div>
              
              {selectedClient && (
                <div className="selected-client-card">
                  <div className="client-info">
                    <strong>{selectedClient.firstName} {selectedClient.lastName}</strong>
                    {selectedClient.businessName && <span> ({selectedClient.businessName})</span>}
                  </div>
                  <div className="client-details">
                    <span><Mail size={14} /> {selectedClient.email || 'No email'}</span>
                    <span><Phone size={14} /> {selectedClient.phone || 'No phone'}</span>
                  </div>
                  <button 
                    className="btn-change"
                    onClick={() => {
                      setSelectedClientId('');
                      setClientSearch('');
                    }}
                  >
                    Change
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="new-client-form">
              <div className="form-row">
                <div className="form-field">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newClientInfo.firstName}
                    onChange={(e) => setNewClientInfo({...newClientInfo, firstName: e.target.value})}
                    placeholder="First name"
                  />
                </div>
                <div className="form-field">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={newClientInfo.lastName}
                    onChange={(e) => setNewClientInfo({...newClientInfo, lastName: e.target.value})}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label><Mail size={14} /> Email *</label>
                  <input
                    type="email"
                    value={newClientInfo.email}
                    onChange={(e) => setNewClientInfo({...newClientInfo, email: e.target.value})}
                    placeholder="client@email.com"
                  />
                </div>
                <div className="form-field">
                  <label><Phone size={14} /> Phone</label>
                  <input
                    type="tel"
                    value={newClientInfo.phone}
                    onChange={(e) => setNewClientInfo({...newClientInfo, phone: e.target.value})}
                    placeholder="(434) 555-1234"
                  />
                </div>
              </div>
              <button 
                className="btn-link"
                onClick={() => {
                  setIsNewClient(false);
                  setNewClientInfo({ firstName: '', lastName: '', email: '', phone: '' });
                }}
              >
                ← Back to client search
              </button>
            </div>
          )}
        </section>

        {/* Property/Service Address */}
        <section className="builder-section">
          <h2><Home size={20} /> Property / Service Address</h2>
          
          <div className="form-field">
            <label>Street Address</label>
            <input
              type="text"
              value={propertyInfo.street}
              onChange={(e) => setPropertyInfo({...propertyInfo, street: e.target.value})}
              placeholder="123 Main Street"
            />
          </div>
          
          <div className="form-row form-row-3">
            <div className="form-field">
              <label>City</label>
              <input
                type="text"
                value={propertyInfo.city}
                onChange={(e) => setPropertyInfo({...propertyInfo, city: e.target.value})}
                placeholder="Charlottesville"
              />
            </div>
            <div className="form-field">
              <label>State</label>
              <input
                type="text"
                value={propertyInfo.state}
                onChange={(e) => setPropertyInfo({...propertyInfo, state: e.target.value})}
                placeholder="VA"
                maxLength={2}
              />
            </div>
            <div className="form-field">
              <label>ZIP</label>
              <input
                type="text"
                value={propertyInfo.zip}
                onChange={(e) => setPropertyInfo({...propertyInfo, zip: e.target.value})}
                placeholder="22901"
              />
            </div>
          </div>
          
          <div className="form-row form-row-3">
            <div className="form-field">
              <label>Square Footage</label>
              <input
                type="text"
                value={propertyInfo.sqft}
                onChange={(e) => setPropertyInfo({...propertyInfo, sqft: e.target.value})}
                placeholder="2500"
              />
            </div>
            <div className="form-field">
              <label>Bedrooms</label>
              <input
                type="number"
                value={propertyInfo.bedrooms}
                onChange={(e) => setPropertyInfo({...propertyInfo, bedrooms: e.target.value})}
                placeholder="3"
                min="0"
              />
            </div>
            <div className="form-field">
              <label>Bathrooms</label>
              <input
                type="number"
                value={propertyInfo.bathrooms}
                onChange={(e) => setPropertyInfo({...propertyInfo, bathrooms: e.target.value})}
                placeholder="2"
                min="0"
                step="0.5"
              />
            </div>
          </div>
          
          <div className="form-field">
            <label>Property Notes</label>
            <input
              type="text"
              value={propertyInfo.propertyNotes}
              onChange={(e) => setPropertyInfo({...propertyInfo, propertyNotes: e.target.value})}
              placeholder="Gate code, access instructions, etc."
            />
          </div>
          
          <div className="checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={useDifferentBilling}
                onChange={(e) => setUseDifferentBilling(e.target.checked)}
              />
              Use different billing address
            </label>
          </div>
          
          {useDifferentBilling && (
            <div className="billing-address-section">
              <h4>Billing Address</h4>
              <div className="form-field">
                <label>Street Address</label>
                <input
                  type="text"
                  value={billingAddress.street}
                  onChange={(e) => setBillingAddress({...billingAddress, street: e.target.value})}
                  placeholder="123 Billing St"
                />
              </div>
              <div className="form-row form-row-3">
                <div className="form-field">
                  <label>City</label>
                  <input
                    type="text"
                    value={billingAddress.city}
                    onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                    placeholder="City"
                  />
                </div>
                <div className="form-field">
                  <label>State</label>
                  <input
                    type="text"
                    value={billingAddress.state}
                    onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                    placeholder="VA"
                    maxLength={2}
                  />
                </div>
                <div className="form-field">
                  <label>ZIP</label>
                  <input
                    type="text"
                    value={billingAddress.zip}
                    onChange={(e) => setBillingAddress({...billingAddress, zip: e.target.value})}
                    placeholder="22901"
                  />
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Estimate Details */}
        <section className="builder-section">
          <h2><FileText size={20} /> Estimate Details</h2>
          
          <div className="form-row">
            <div className="form-field flex-2">
              <label>Estimate Title</label>
              <input
                type="text"
                value={estimateTitle}
                onChange={(e) => setEstimateTitle(e.target.value)}
                placeholder="e.g., Commercial Cleaning Quote"
              />
            </div>
            <div className="form-field">
              <label>Valid For (days)</label>
              <input
                type="number"
                value={validDays}
                onChange={(e) => setValidDays(parseInt(e.target.value) || 30)}
                min={1}
                max={90}
              />
            </div>
          </div>
        </section>

        {/* Line Items */}
        <section className="builder-section line-items-section">
          <div className="section-header">
            <h2><Calculator size={20} /> Line Items</h2>
            <div className="section-actions">
              <button className="btn-preset" onClick={() => setShowPresets(!showPresets)}>
                {showPresets ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                Quick Add
              </button>
              <button className="btn-add" onClick={addLineItem}>
                <Plus size={16} /> Add Line
              </button>
            </div>
          </div>

          {showPresets && (
            <div className="presets-panel">
              {PRESET_ITEMS.map(category => (
                <div key={category.category} className="preset-category">
                  <button
                    className="category-header"
                    onClick={() => setExpandedCategory(expandedCategory === category.category ? null : category.category)}
                  >
                    {category.category}
                    {expandedCategory === category.category ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {expandedCategory === category.category && (
                    <div className="preset-items">
                      {category.items.map(item => (
                        <button key={item.description} className="preset-item" onClick={() => addPresetItem(item)}>
                          <span>{item.description}</span>
                          <span className="preset-price">{item.price > 0 ? formatCurrency(item.price) : 'Custom'}/{item.uom}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="line-items-table">
            <div className="table-header">
              <div className="col-description">Description</div>
              <div className="col-uom">UOM</div>
              <div className="col-qty">Qty</div>
              <div className="col-price">Unit Price</div>
              <div className="col-total">Total</div>
              <div className="col-actions"></div>
            </div>
            
            {lineItems.map((item) => (
              <div key={item.id} className="line-item-row">
                <div className="col-description">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                    placeholder="Service description..."
                  />
                </div>
                <div className="col-uom">
                  <select value={item.uom} onChange={(e) => updateLineItem(item.id, 'uom', e.target.value)}>
                    {UOM_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="col-qty">
                  <input
                    type="number"
                    value={item.qty}
                    onChange={(e) => updateLineItem(item.id, 'qty', parseFloat(e.target.value) || 0)}
                    min={0}
                    step={item.uom === 'sqft' ? 100 : 1}
                  />
                </div>
                <div className="col-price">
                  <div className="price-input">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => updateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                      min={0}
                      step={0.01}
                    />
                  </div>
                </div>
                <div className="col-total">{formatCurrency(item.qty * item.unitPrice)}</div>
                <div className="col-actions">
                  <button className="btn-icon" onClick={() => duplicateLineItem(item)} title="Duplicate"><Copy size={14} /></button>
                  <button className="btn-icon danger" onClick={() => removeLineItem(item.id)} title="Remove" disabled={lineItems.length === 1}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Discount Section */}
          <div className="discount-section">
            <h4><Percent size={16} /> Discount</h4>
            <div className="discount-controls">
              <select value={discountType} onChange={(e) => { setDiscountType(e.target.value); if (e.target.value === 'none') setDiscountValue(0); }}>
                <option value="none">No Discount</option>
                <option value="percent">Percentage (%)</option>
                <option value="fixed">Fixed Amount ($)</option>
              </select>
              
              {discountType !== 'none' && (
                <>
                  <div className="discount-input">
                    <span>{discountType === 'percent' ? '%' : '$'}</span>
                    <input
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                      min={0}
                      max={discountType === 'percent' ? 100 : subtotal}
                    />
                  </div>
                  <input
                    type="text"
                    className="discount-reason-input"
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder="Reason (optional)"
                  />
                </>
              )}
            </div>
          </div>

          {/* Totals */}
          <div className="totals-section">
            <div className="total-row subtotal">
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="total-row discount">
                <span>Discount {discountType === 'percent' ? `(${discountValue}%)` : ''}{discountReason && ` - ${discountReason}`}</span>
                <span>-{formatCurrency(discountAmount)}</span>
              </div>
            )}
            <div className="total-row grand-total">
              <span>Total</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>
        </section>

        {/* Notes */}
        <section className="builder-section">
          <h2>Notes / Terms</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional notes, terms, or conditions..."
            rows={4}
          />
        </section>

        {/* Actions */}
        <div className="builder-actions">
          {isEditing && (
            <button 
              className="btn btn-danger-outline" 
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 size={18} /> Delete
            </button>
          )}
          <div className="actions-right">
            {saveSuccess && <span className="save-success">✓ Draft saved!</span>}
            <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={isSaving}>
              {isSaving ? 'Saving...' : <><Save size={18} /> Save Draft</>}
            </button>
            <button className="btn btn-secondary" onClick={() => setPreviewMode(true)}>
              <Eye size={18} /> Preview
            </button>
            <button className="btn btn-primary" onClick={handleSend} disabled={isSending}>
              {isSending ? 'Sending...' : <><Send size={18} /> Send to Client</>}
            </button>
          </div>
        </div>
      </div>
      
      {previewMode && <PreviewModal />}
      
      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => { setShowDeleteModal(false); setDeleteConfirmChecked(false); }}>
          <div className="delete-modal-container" onClick={e => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h2><Trash2 size={20} /> Delete Estimate</h2>
              <button className="btn-close" onClick={() => { setShowDeleteModal(false); setDeleteConfirmChecked(false); }}>
                <X size={20} />
              </button>
            </div>
            
            <div className="delete-modal-content">
              {/* Warning for approved/converted estimates */}
              {(existingEstimate?.status === 'approved' || existingEstimate?.status === 'converted') && (
                <div className="delete-warning critical">
                  <AlertTriangle size={24} />
                  <div>
                    <strong>Warning: This estimate has been {existingEstimate?.status}!</strong>
                    <p>
                      {isAdmin 
                        ? 'As an admin, you can delete this estimate, but it may affect related jobs or invoices.'
                        : 'Only administrators can delete approved or converted estimates.'}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Standard warning */}
              <div className="delete-warning">
                <AlertTriangle size={20} />
                <div>
                  <strong>This action cannot be undone!</strong>
                  <p>Deleting this estimate will permanently remove it from the system.</p>
                </div>
              </div>
              
              {/* Estimate summary */}
              <div className="delete-summary">
                <p><strong>Estimate:</strong> {estimateTitle}</p>
                <p><strong>Client:</strong> {selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : 'Unknown'}</p>
                <p><strong>Status:</strong> {existingEstimate?.status || 'draft'}</p>
                <p><strong>Total:</strong> {formatCurrency(total)}</p>
              </div>
              
              {/* Confirmation checkbox */}
              {(isAdmin || (existingEstimate?.status !== 'approved' && existingEstimate?.status !== 'converted')) && (
                <label className="delete-confirm-checkbox">
                  <input
                    type="checkbox"
                    checked={deleteConfirmChecked}
                    onChange={(e) => setDeleteConfirmChecked(e.target.checked)}
                  />
                  <span>I understand this action is permanent and cannot be undone</span>
                </label>
              )}
              
              <div className="delete-modal-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => { setShowDeleteModal(false); setDeleteConfirmChecked(false); }}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                
                {(isAdmin || (existingEstimate?.status !== 'approved' && existingEstimate?.status !== 'converted')) ? (
                  <button 
                    className="btn btn-danger"
                    onClick={handleDeleteEstimate}
                    disabled={!deleteConfirmChecked || isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <Loader size={18} className="spinning" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={18} />
                        Delete Estimate
                      </>
                    )}
                  </button>
                ) : (
                  <button className="btn btn-danger" disabled>
                    <Trash2 size={18} />
                    Admin Only
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomEstimate;
